import webuiState from "../app/InternalStateService";
import { extractStorePath, getDependentPaths, getStorePathWidgetId } from "../utils/storeUtils";
import { addQueryParams } from "../utils/urlUtils";
import { recordUpdate, isObsoleteUpdate, finishUpdate } from "../utils/updateActionUtils";
import { loadMissingComponents, loadInnerWidgets } from "./Loader";
import {
    setErrorState,
    setWidgetContext,
    WIDGET_REQUEST,
    WIDGET_SUCCESS,
    WIDGET_DATA_REQUEST,
    WIDGET_DATA_SUCCESS
} from "./Widget";
import promiseAllSettled from "../utils/promiseAllSettled";
import { triggerExitConfirm } from "./Dashboard";
import translationService from "../app/TranslationService";
import { constructRequestEntity } from "../utils/dataUtils";
import { WEBUILogs } from "@tinqin/tinqin-utils/src/logging";

export const DATA_SET = "DATA_SET";
export const DATA_MERGE = "DATA_MERGE";

//Summary:
//General purpose action for making a single request/response cycle for updating client data
//-can perform client side validation, trigger loading animations, send/update arbitrary client data
//-can be executed inside Widget and inside a Step without change of contract (FE implementation)
//Behaviour:
//-format data for the POST request depending on the sources defined
//-manage the response depending on the targets defined (the response will be set to all the targets)
export function update(sirenAction, currentWidgetId, currentPath) {
    // const clearUiStateValidationsDeep = webuiState.getActionByName(
    //     "component.clearUiStateValidationsDeep"
    // );
    let actionType = sirenAction.actionType || "update";
    let updateActionDefaultConfig =
        webuiState.getActionsDefaultConfig({ widgetId: currentWidgetId })[actionType] || {};
    //configure on project level to check for actions with same id
    //TBD the strategy for these actions - to be put in a queue or only firs/last should be executed, etc.
    let synchronizeActions = updateActionDefaultConfig.synchronizeActions;
    return async (dispatch, getState) => {
        let {
            id: actionId,
            //POST/GET
            //default: POST
            method = "POST",
            //default: currentWidget url
            href = "",
            //default headers
            headers = {
                "Content-Type": sirenAction.type || sirenAction.contentType || "application/json"
            },
            properties = {}
        } = sirenAction;

        let actionResult;
        let requestPayload = {};
        let response;
        let currentWidgetEntity = getState().getIn(["data", currentWidgetId, "class", 0]);

        //active flow open confirm message
        let activeFlowMessage;
        if (properties.hasOwnProperty("flowContext")) {
            let flowContext = properties.flowContext || {};
            activeFlowMessage = flowContext.exitConfirm;
        }

        //if no sources provided - default is current widget's root entity widgetId::rootEntityId
        //if sources is provided, but it is an empty array - no content will be send to the server
        //if sources is provided - requested content will be send to the server
        let sources = [currentWidgetId + "::" + currentWidgetEntity];
        // preserving current behaviour
        // body of the POST request to be a single entity, instead of "sourceId": {content}
        // case of "sources": undefined, to send only single entity in POST body request
        let singleEntityInRequestBody = true;
        if (sirenAction.hasOwnProperty("sources")) {
            singleEntityInRequestBody = false;
            sources = sirenAction.sources.length ? sirenAction.sources : [];
        }

        //if no targets provided - default is current widget
        //if targets is provided, but it is an empty array - response will not be saved
        //if targets is provided - content will be saved at target locations, unless a special response is returned
        let targets = [currentWidgetEntity];
        if (sirenAction.hasOwnProperty("targets")) {
            targets = sirenAction.targets.length ? sirenAction.targets : [];
        }

        //used to prevent response data to be saved on client side (for POST only requests)
        let ignoreResponse = targets.length ? false : true;

        //default endpoint
        if (!href) {
            href = webuiState.getActionByName("widget.getWidgetUrl")(currentWidgetId, getState());
        }

        if (method === "GET") {
            if (sirenAction.parameters) {
                href = addQueryParams(href, sirenAction.parameters);
            }
        }
        //construct request payload
        if (method === "POST") {
            requestPayload = constructRequestEntity(
                sources,
                getState(),
                currentPath,
                sirenAction,
                currentWidgetId,
                { singleEntityInRequestBody }
            );
        }

        //for synchronisation purpose between update actions with the same id
        let actionIndex;
        if (actionId && synchronizeActions) {
            //check for actions with same id from current widget
            actionIndex = dispatch(recordUpdate(`${currentWidgetId}.${actionId}`));
        }
        let loadingType = updateActionDefaultConfig.loadingType || targets;
        //send server request
        try {
            if (activeFlowMessage) {
                //check flow target is different from targets
                await dispatch(triggerExitConfirm(sirenAction));
            }
            //trigger loading animations
            await dispatch(
                triggerLoadingAnimations(loadingType, currentPath, targets, true, currentWidgetId)
            );
            //update action
            //reset flow target - case on completeFlow
            if (properties.hasOwnProperty("flowContext")) {
                await dispatch(
                    webuiState.getActionByName("flow.triggerSetFlowContext")(properties.flowContext)
                );
            }
            response = await dispatch(
                webuiState.getActionByName("request.request")(
                    {
                        method: method,
                        endpoint: href,
                        headers: headers,
                        payload: requestPayload
                    },
                    {
                        widgetId: currentWidgetId
                    }
                )
            );
            let isObsolete = false;
            //for synchronisation purpose between update actions with the same id
            if (actionId && synchronizeActions) {
                //check for actions with same id from current widget
                isObsolete = dispatch(
                    isObsoleteUpdate({ actionId: `${currentWidgetId}.${actionId}`, actionIndex })
                );
            }

            //save response from server
            //or the action with the same id but executed later is ongoing
            //or response contains actions for dispatch
            let dispatchActions =
                response.hasOwnProperty("actions") && response.hasOwnProperty("status");
            if (ignoreResponse || isObsolete || dispatchActions) {
                actionResult = response;
            } else {
                actionResult = await dispatch(
                    saveResponse(response, targets, getState(), currentPath, currentWidgetId)
                );
            }
        } catch (response) {
            //manage business failure with update of the content
            if (response.hasOwnProperty("content")) {
                await dispatch(
                    saveResponse(
                        response.content,
                        targets,
                        getState(),
                        currentPath,
                        currentWidgetId
                    )
                );
            }
            if (response.name === "ApiError") {
                dispatch(updateFailure(response));
            }
            actionResult = Promise.reject(response);
        } finally {
            //for synchronisation purpose between update actions with the same id
            if (actionId) {
                dispatch(finishUpdate({ actionId, actionIndex }));
            }
            dispatch(
                triggerLoadingAnimations(loadingType, currentPath, targets, false, currentWidgetId)
            );
        }
        return actionResult;
    };
}

export function updateFlow(sirenAction, currentWidgetId, currentPath) {
    let actionType = sirenAction.actionType || "updateFlow";
    let updateActionDefaultConfig =
        webuiState.getActionsDefaultConfig({ widgetId: currentWidgetId })[actionType] || {};
    return async (dispatch, getState) => {
        let {
            //POST/GET
            //default: POST
            method = "POST",
            //default: currentWidget url
            href = "",
            //default headers
            headers = {
                "Content-Type": sirenAction.type || sirenAction.contentType || "application/json"
            },
            properties = {}
        } = sirenAction;

        let actionResult;
        let requestPayload = {};
        let response;
        let currentWidgetEntity = getState().getIn(["data", currentWidgetId, "class", 0]);

        //active flow open confirm message
        let activeFlowMessage;
        if (properties.hasOwnProperty("flowContext")) {
            let flowContext = properties.flowContext || {};
            activeFlowMessage = flowContext.exitConfirm;
        }

        //if no sources provided - default is current widget
        //if sources is provided, but it is an empty array - no content will be send to the server
        //if sources is provided - requested content will be send to the server
        let sources = [currentWidgetEntity];
        if (sirenAction.hasOwnProperty("sources")) {
            sources = sirenAction.sources.length ? sirenAction.sources : [];
        }
        //if no targets provided - default is current widget
        //if targets is provided, but it is an empty array - no content will be send to the server
        //if targets is provided - requested content will be send to the server
        let targets = [currentWidgetEntity];
        if (sirenAction.hasOwnProperty("targets")) {
            targets = sirenAction.targets.length ? sirenAction.targets : [];
        }

        //default endpoint
        if (!href) {
            href = webuiState.getActionByName("widget.getWidgetUrl")(currentWidgetId, getState());
        }

        if (method === "GET") {
            if (sirenAction.parameters) {
                href = addQueryParams(href, sirenAction.parameters);
            }
        }
        //construct request payload
        if (method === "POST") {
            requestPayload = constructRequestEntity(
                sources,
                getState(),
                currentPath,
                sirenAction,
                currentWidgetEntity
            );
        }
        let loadingType = updateActionDefaultConfig.loadingType || targets;
        //send server request
        try {
            if (activeFlowMessage) {
                //check flow target is different from targets
                await dispatch(triggerExitConfirm(sirenAction));
            }

            //trigger loading animations
            await dispatch(
                triggerLoadingAnimations(loadingType, currentPath, targets, true, currentWidgetId)
            );

            //update flow action
            //reset flow target - case on completeFlow
            if (properties.hasOwnProperty("flowContext")) {
                await dispatch(
                    webuiState.getActionByName("flow.triggerSetFlowContext")(properties.flowContext)
                );
            }
            response = await dispatch(
                webuiState.getActionByName("request.request")(
                    {
                        method: method,
                        endpoint: href,
                        headers: headers,
                        payload: requestPayload
                    },
                    {
                        widgetId: currentWidgetId
                    }
                )
            );

            //save response from server
            //or the action with the same id but executed later is ongoing
            //or response contains actions for dispatch
            let dispatchActions =
                response.hasOwnProperty("actions") && response.hasOwnProperty("status");

            if (dispatchActions) {
                actionResult = response;
            } else {
                actionResult = await dispatch(
                    saveResponse(response, targets, getState(), currentPath, currentWidgetId)
                );
            }
        } catch (response) {
            //manage business failure with update of the content
            if (response.hasOwnProperty("content")) {
                await dispatch(
                    saveResponse(
                        response.content,
                        targets,
                        getState(),
                        currentPath,
                        currentWidgetId
                    )
                );
            }

            if (response.name === "ApiError") {
                dispatch(updateFailure(response));
            }

            actionResult = Promise.reject(response);
        } finally {
            //trigger loading animations
            await dispatch(
                triggerLoadingAnimations(loadingType, currentPath, targets, false, currentWidgetId)
            );
        }
        return actionResult;
    };
}

export function loadWidgetContainer(widgetConfig, skipLoadingAnimation = false, queryParameters, waitInnerWidgetsLoading) {
    return async (dispatch, getState) => {
        let widgetDataLoaded = getState().getIn(["data", widgetConfig.id]);
        let cached = widgetConfig.cached;
        //cached possible values:
        //undefined = false = "none", "config", "data", "all" = true
        //"config" and "data" to be developed (no usecase available)
        if ((cached === "all" || cached === true) && widgetDataLoaded) {
            return;
        }
        let widgetResource = widgetConfig.href;
        let id = widgetConfig.id;
        let dataUrl = addQueryParams(widgetResource, queryParameters);
        let urlParams = "";
        let configResource = `${widgetResource}/configuration`;
        if (widgetResource.includes("?")) {
            let widgetResourceSplit = widgetResource.split("?");
            urlParams = widgetResourceSplit[1];
            configResource = `${widgetResourceSplit[0]}/configuration?${urlParams}`;
        }
        let configUrl = addQueryParams(configResource, queryParameters);
        let parentWidget = widgetConfig.parentWidget;
        if (!skipLoadingAnimation) {
            await dispatch(webuiState.getActionByName("loading.triggerWidgetLoading")(id, true));
        }
        let result = await Promise.all([
            dispatch(loadWidgetContainerConfig(configUrl, id, dataUrl)),
            dispatch(loadWidgetContainerData(dataUrl, id, parentWidget, waitInnerWidgetsLoading))
        ]);

        if (!skipLoadingAnimation) {
            dispatch(webuiState.getActionByName("loading.triggerWidgetLoading")(id, false));
        }
        return result;
    };
}

function fetchWidgetContainerConfig(endpoint, widget, dataUrl, size) {
    let { WIDGETS_ROOT } = webuiState.getWebConfig();
    let widgetId = widget;
    let configEndpoint = endpoint ? endpoint : WIDGETS_ROOT + `/${widget}/configuration`;
    if (webuiState.getGlobalProperties("screenSizeQueryParameter")) {
        if (size) {
            let concatSymbol = configEndpoint.indexOf("?") !== -1 ? "&" : "?";
            configEndpoint += `${concatSymbol}screenSize=${size}`;
        }
    }
    return async (dispatch) => {
        let actionResult;
        try {
            await dispatch({
                type: WIDGET_REQUEST
            });
            let options = {
                method: "GET",
                endpoint: configEndpoint
            };
            const { STATIC_CONFIGURATIONS_ROOT } = webuiState.getWebConfig();
            if (STATIC_CONFIGURATIONS_ROOT) {
                options.endpoint =
                    STATIC_CONFIGURATIONS_ROOT + configEndpoint.split("?")[0] + ".json";
                options.rawEndpointUrl = true;
            }
            const urlParser = webuiState.getStaticConfigUrlParser({ widgetId });
            if (urlParser) {
                options.endpoint = await urlParser({
                    context: "widget",
                    endpoint: configEndpoint,
                    id: widgetId
                });
                options.rawEndpointUrl = true;
            }
            //client application could be responsible for loading static configuration
            let staticConfigLoader = webuiState.getStaticConfigLoader({ widgetId });
            const lazyModuleLoader = webuiState.getLazyModuleLoader({ widgetId });
            if (staticConfigLoader) {
                actionResult = await staticConfigLoader.load({
                    id: widgetId,
                    type: "widgetContainer",
                    defaultRequestOptions: options
                });
            } else if (lazyModuleLoader) {
                let module = await lazyModuleLoader.load({
                    id: widgetId,
                    type: "widgetContainer"
                });
                actionResult = module.config;
                //widget module can set widget context
                if (module.context) {
                    setWidgetContext(widgetId, module.contest);
                }
            } else {
                //default behaviour is to make a request for UI configuration
                actionResult = await dispatch(
                    webuiState.getActionByName("request.request")(options, {
                        widgetId: widgetId
                    })
                );
            }
            actionResult = await loadMissingComponents({ config: actionResult, widgetId });
            dispatch({
                type: WIDGET_SUCCESS,
                payload: Object.assign({}, actionResult, {
                    widget: widgetId,
                    endpoint: dataUrl
                })
            });
        } catch (error) {
            WEBUILogs.error(error);
            setErrorState(error, widget, dispatch);
            actionResult = Promise.reject(error);
        }
        return actionResult;
    };
}

export function loadWidgetContainerConfig(configEndpoint, widget, dataUrl) {
    return (dispatch, getState) => {
        if (widget) {
            let size = getState().getIn(["windowSize", "size"]);
            return dispatch(fetchWidgetContainerConfig(configEndpoint, widget, dataUrl, size));
        } else {
            return Promise.reject("Promise: LoadWidgetConfig action failed.");
        }
    };
}

function fetchWidgetContainerData(
    endpoint = "",
    widget,
    parentWidget,
    waitInnerWidgetsLoading = false
) {
    return async (dispatch, getState) => {
        let actionResult;
        const loadWidget = webuiState.getActionByName("widget.loadWidget");
        let dataEndpoint = endpoint;
        if (webuiState.getGlobalProperties("screenSizeQueryParameter")) {
            let size = getState().getIn(["windowSize", "size"]);
            if (size) {
                let concatSymbol = endpoint.indexOf("?") !== -1 ? "&" : "?";
                dataEndpoint = `${endpoint}${concatSymbol}screenSize=${size}`;
            }
        }
        try {
            await dispatch({
                type: WIDGET_DATA_REQUEST
            });
            actionResult = await dispatch(
                webuiState.getActionByName("request.request")(
                    {
                        method: "GET",
                        endpoint: dataEndpoint
                    },
                    {
                        widgetId: widget
                    }
                )
            );
            if (waitInnerWidgetsLoading) {
                await loadInnerWidgets(dispatch, widget, actionResult, { waitInnerWidgetsLoading });
            } else {
                loadInnerWidgets(dispatch, widget, actionResult);
            }
            //actionResult = await applyDataParser(dispatch, getState, actionResult, widget);
            dispatch({
                type: WIDGET_DATA_SUCCESS,
                payload: Object.assign({}, actionResult, {
                    widget: widget,
                    parentWidget: parentWidget
                })
            });
            if (actionResult.widgets) {
                // let parent = widget;
                // if (parentWidget && widget !== parentWidget) {
                //     parent = `${parentWidget}.${widget}`;
                // }
                let skipAnimation = getState().getIn(["config", widget, "skipWidgetsAnimation"]);
                promiseAllSettled(
                    actionResult.widgets.map((widgetConfig) => {
                        return dispatch(
                            loadWidget(
                                Object.assign({ parentWidget: widget }, widgetConfig),
                                skipAnimation
                            )
                        );
                    })
                );
            }
        } catch (error) {
            WEBUILogs.error(error);
            setErrorState(error, widget, dispatch);
            actionResult = Promise.reject(error);
        }
        return actionResult;
    };
}

export function loadWidgetContainerData(endpoint, widget, parentWidget, waitInnerWidgetsLoading) {
    return (dispatch) => {
        if (widget) {
            return dispatch(fetchWidgetContainerData(endpoint, widget, parentWidget, waitInnerWidgetsLoading));
        } else {
            return Promise.reject("Promise: ProcessStep action failed.");
        }
    };
}

//returns the following format if no sources defined
// {
//     "action": {
//      "id": "searchClient",
//         "type": "update",
//         "parameters": {
//         "key1": "value1"
//     }
// },
//     "content": {
//     "class": [
//         "quickSearch"
//     ],
//         "entities": [
//         {
//             "class": [
//                 "criteriaPanels"
//             ]
//         }
//     ]
// }
// }

//if sources defined the content contains the path:
// "content": {
//     "quickSearch.criteriaPanels.searchClient": {
//         "class": [
//             "searchClient"
//         ],
//             "entities": [
//             {
//                 "class": [
//                     "clientNumber"
//                 ],
//                 "properties": {
//                     "value": "12344"
//                 }
//             }
//         ]
//     }
// }

export function saveResponse(response, targets, state, currentPath, currentWidgetId) {
    //allows multiple target entities to be updated with different data
    if (isCollectiveResponse(response)) {
        delete response.collectiveResponse;
        return saveCollectiveResponse(response, state, currentPath, currentWidgetId);
    }
    return updateEntity(response, targets, state, currentPath, currentWidgetId);
}

export function triggerLoadingAnimations(
    loadingType,
    currentPath,
    targets = ["{widget}"],
    triggerFlag,
    currentWidgetId
) {
    const triggerLoading = webuiState.getActionByName("loading.triggerLoading");
    return (dispatch, getState) => {
        let loadingTargets = loadingType || targets;
        // if (!targets.length) {
        //     loadingTargets = ["{widget}"];
        // }
        if (loadingTargets.length === 1 && loadingTargets[0] === "{root}") {
            return dispatch(
                webuiState.getActionByName("loading.triggerGlobalLoading")(triggerFlag)
            );
        }
        if (loadingTargets.length === 1 && loadingTargets[0] === "{widget}") {
            return dispatch(
                webuiState.getActionByName("loading.triggerWidgetLoading")(
                    currentWidgetId,
                    triggerFlag
                )
            );
            //loadingTargets = [currentWidgetId];
        }
        if (loadingTargets.length === 1 && loadingTargets[0] === "{self}") {
            loadingTargets = [currentPath];
        }
        //case targets = [] are provided from server
        if (!loadingTargets.length) {
            return Promise.resolve();
        }
        return dispatch(triggerLoading(loadingTargets, currentPath, triggerFlag, currentWidgetId));
    };
}
//check for special response entity in the following format
//Example:
// {
// 	collectiveResponse: true,
//  "widget.cardDescription": {
//      class: ["cardDescription"],
//      ......
//  },
//  "widget.cardDescription1": {
//      class: ["cardDescription1"],
//      ......
//  },
// }
//
function isCollectiveResponse(response = {}) {
    //return response["class"] ? response["class"][0] === "response" : false;
    return response.hasOwnProperty("collectiveResponse");
}

//allows multiple target entities to be updated with different data
function saveCollectiveResponse(response, state, currentPath, currentWidgetId) {
    return (dispatch) => {
        for (let key in response) {
            dispatch(updateEntity(response[key], [key], state, currentPath, currentWidgetId));
        }
    };
}

function updateEntity(newData, targets, state, currentPath, currentWidgetId) {
    return (dispatch, getState) => {
        //The entity of type widget should merge data
        //The widget's entities should set data - merge of data does not overwrite the data
        //case when we want to clear an entity BE send us only the entity class
        // let widgetId = checkEntityIsWidget(getState(), targets[0]);
        // if (targets.length === 1 && widgetId) {
        //     return dispatch(triggerUpdateMerge([[widgetId]], newData));
        // }
        let targetEntities = getDependentPaths(targets, state, currentPath, currentWidgetId);
        if (!targetEntities.length) {
            WEBUILogs.warn("updateAction: Missing response targets:");
            WEBUILogs.warn(targets);
            return Promise.reject("updateAction: Missing response targets.");
        }
        targetEntities.forEach((item) => {
            let widgetId = getStorePathWidgetId(item);
            //asynchronous load of inner widgets
            //for each storePath, the data is checked for inner widgets and they are loaded
            //warning: it is possible this way to add same widget twice within the same parent, causing unknown behaviour
            loadInnerWidgets(dispatch, widgetId, newData);
        });
        // return dispatch(triggerUpdateSet(targetEntities, newData));
        //temp solution in order to avoid clearing context data like "parentWidget" and "context" when updating
        //top level entities
        let topLevelTargets = [];
        let innerTargets = [];
        targetEntities.forEach((item) => {
            if (item.length > 1) {
                innerTargets.push(item);
            } else {
                topLevelTargets.push(item);
            }
        });
        dispatch(triggerUpdateSet(innerTargets, newData));
        dispatch(triggerUpdateMerge(topLevelTargets, newData));
    };
}

function triggerUpdateMerge(targets, data) {
    return {
        type: DATA_MERGE,
        payload: {
            targets,
            data
        }
    };
}

function triggerUpdateSet(targets, data) {
    return {
        type: DATA_SET,
        payload: {
            targets,
            data
        }
    };
}

export function updateEntities(sirenAction) {
    //target is the parent
    let parentEntity = sirenAction.targets || [];
    if (!parentEntity.length) {
        return Promise.reject("updateEntities action failed: missing targets.");
    }
    let properties = sirenAction.properties || {};
    // properties: {
    //     visible: {
    //         value: true,
    //             strategy: inclusive, exclusive
    //         entities: ["carValue", "clauseList"]
    //     },
    //     editable: {
    //         value: true,
    //             strategy: inclusive, exclusive
    //         entities: ["carValue"]
    //     },
    //     required: {
    //         value: true,
    //             strategy: inclusive, exclusive
    //         entities: ["carValue"]
    //     }
    // }

    return (dispatch, getState) => {
        let state = getState();
        for (let key in properties) {
            let stateUpdated = false;
            let { value, strategy = "exclusive" } = properties[key];
            let dependentStorePath = extractStorePath(parentEntity[0], state);
            let entitiesState = state.getIn(["data", ...dependentStorePath, "entities"]);
            //the absence of the entities means that all entities will be updated
            if (!properties[key].hasOwnProperty("entities")) {
                if (entitiesState && entitiesState.size) {
                    stateUpdated = true;
                    entitiesState.forEach((entity, index) => {
                        let newProperty = {};
                        newProperty[key] = value;
                        let targetEntity = [...dependentStorePath, "entities", index];
                        dispatch(
                            webuiState.getActionByName("component.triggerMultipleSetProperties")(
                                [targetEntity],
                                newProperty
                            )
                        );
                    });
                }
            }
            //empty array means nothing will be updated
            let entities = properties[key]["entities"] || [];
            if (!entities.length && !stateUpdated) {
                stateUpdated = true;
                WEBUILogs.warn("Update entities action expects entities to update!");
            }
            //strategy is exclusive(apply the property, for the rest children apply !property)
            //strategy is inclusive(apply the property, do not change other children properties)
            if (entitiesState && entitiesState.size && !stateUpdated) {
                entitiesState.forEach((entity, index) => {
                    let newProperty = {};
                    newProperty[key] = value;
                    let entityClass = entity.getIn(["class", 0]);
                    let targetEntity = [...dependentStorePath, "entities", index];
                    if (entities.includes(entityClass) || strategy === "completely") {
                        dispatch(
                            webuiState.getActionByName("component.triggerMultipleSetProperties")(
                                [targetEntity],
                                newProperty
                            )
                        );
                    }
                    if (!entities.includes(entityClass) && strategy === "exclusive") {
                        newProperty[key] = !value;
                        dispatch(
                            webuiState.getActionByName("component.triggerMultipleSetProperties")(
                                [targetEntity],
                                newProperty
                            )
                        );
                    }
                });
            }
        }
        return Promise.resolve();
    };
}

export function filterEntities(sirenAction) {
    //target is the container
    let parentEntity = sirenAction.targets || [];
    if (!parentEntity.length) {
        return Promise.reject("updateEntities action failed: missing targets.");
    }
    let properties = sirenAction.properties || {};

    return async (dispatch, getState) => {
        let state = getState();
        let showAll = false;
        let value = false;
        //the absence of the entities means that all entities will reject filter
        if (!properties.hasOwnProperty("entities")) {
            showAll = true;
            value = true;
        }
        let { entities = [] } = properties;

        let newProperty = {};
        newProperty["visible"] = value;

        let dependentStorePath = extractStorePath(parentEntity[0], state);
        let containerEntitiesState = state.getIn(["data", ...dependentStorePath, "entities"]);

        function* findHiddenEntities(entitiesState) {
            if (!entitiesState) {
                return;
            }

            for (let i = 0; i < entitiesState.size; i++) {
                let entity = entitiesState.get(i);
                let entityVisibility = entity.getIn(["properties", "visible"]);
                let entityClass = entity.getIn(["class", 0]);
                if (entityVisibility === false) {
                    yield extractStorePath(entityClass, state);
                }

                if (entity.get("entities")) {
                    yield* findHiddenEntities(entity.get("entities"));
                }
            }
        }

        if (showAll) {
            let entitiesToShow = [];
            let foundEntities = findHiddenEntities(containerEntitiesState);
            let foundGenerator = foundEntities.next();
            while (!foundGenerator.done) {
                if (foundGenerator.value) {
                    entitiesToShow.push(...foundGenerator.value);
                }
                foundGenerator = foundEntities.next();
            }
            if (entitiesToShow.length) {
                dispatch(
                    webuiState.getActionByName("component.triggerMultipleSetProperties")(
                        entitiesToShow,
                        newProperty
                    )
                );
            }
            return Promise.resolve();
        }

        function* processData(entitiesState, parentEntity) {
            if (!entitiesState) {
                return;
            }

            for (let i = 0; i < entitiesState.size; i++) {
                let entity = entitiesState.get(i);
                let entityClass = entity.getIn(["class", 0]);
                if (!parentEntity) {
                    parentEntity = [...dependentStorePath];
                }

                if (entities.includes(entityClass)) {
                    yield {
                        parentEntity: parentEntity,
                        visibleEntity: extractStorePath(entityClass, state)
                    };
                }

                if (entity.get("entities")) {
                    yield* processData(entity.get("entities"), [...parentEntity, "entities", i]);
                }
            }
        }

        let visibleEntities = processData(containerEntitiesState);
        let visibleGenerator = visibleEntities.next();
        let hiddenEntities = [];
        let visibleEntitiesRefresh = [];
        while (!visibleGenerator.done) {
            WEBUILogs.log(visibleGenerator.value);
            let parentEntity = visibleGenerator.value.parentEntity;
            hiddenEntities.push(Array.from(parentEntity));
            let firstLevelEntity =
                dependentStorePath.length === parentEntity.length &&
                dependentStorePath.every((v, i) => v === parentEntity[i]);
            if (!firstLevelEntity) {
                let containerPath = parentEntity.slice(-parentEntity.length, -2);
                visibleEntitiesRefresh.push(containerPath);
            }
            visibleEntitiesRefresh.push(visibleGenerator.value.visibleEntity[0]);
            visibleGenerator = visibleEntities.next();
        }

        if (hiddenEntities.length) {
            let entitiesToHide = [];
            let parentIndexes = [];
            hiddenEntities.forEach((entityParentPath) => {
                let entitiesState = state.getIn(["data", ...entityParentPath, "entities"]);

                entitiesState.forEach((entity, index) => {
                    let entityClass = entity.getIn(["class", 0]);
                    if (!entities.includes(entityClass)) {
                        entitiesToHide.push([...entityParentPath, "entities", index]);
                    }
                    let firstLevelEntity =
                        dependentStorePath.length === entityParentPath.length &&
                        dependentStorePath.every((v, i) => v === entityParentPath[i]);
                    if (!firstLevelEntity) {
                        let containerPath = entityParentPath.slice(-entityParentPath.length, -2);
                        let containerIndex = containerPath[containerPath.length - 1];
                        parentIndexes.push(containerIndex);
                    }
                });
            });
            let uniqueItems = [...new Set(parentIndexes)];
            if (uniqueItems.length) {
                containerEntitiesState.forEach((container, index) => {
                    if (!uniqueItems.includes(index)) {
                        entitiesToHide.push([...dependentStorePath, "entities", index]);
                    }
                });
            }

            await dispatch(
                webuiState.getActionByName("component.triggerMultipleSetProperties")(
                    visibleEntitiesRefresh,
                    { visible: true }
                )
            );

            dispatch(
                webuiState.getActionByName("component.triggerMultipleSetProperties")(
                    entitiesToHide,
                    newProperty
                )
            );
        }

        return Promise.resolve();
    };
}

function updateFailure(error) {
    return (dispatch) => {
        let message = translationService.translate("serverErrorMessage");
        let messages = {
            type: "error",
            message: error.message || message
        };
        return dispatch(webuiState.getActionByName("messages.triggerErrorMessage")(messages));
    };
}
